import { iterableObject } from '../hybrid/utils/iterator.js';
import { createRandomIdentifier } from '../hybrid/utils/random-identifier.js';

export default class Provider {
	pending = iterableObject();
	offlineQueue = [];

	constructor() {
		// kick unanswered requests after 5 min
		setInterval(() => {
			for (const id in this.pending) {
				const req = this.pending[id];
				if (req.time + 300_000 < Date.now()) {
					this.pending[id].reject({ error: true, code: 'timeout' });
					delete this.pending[id];
				}
			}
		}, 10000);
	}

	async request(component, file, func, data, args, offlineFunc, $syncConflict) {
		if (webSocket.isSocketConnected) {
			const remote = await this._wsRequest(component, file, func, data, args);
			if (offlineFunc && $syncConflict) {
				const local = await offlineFunc(data, ...(args || []));
				if (remote?.$hash !== local?.$hash) {
					return $syncConflict(local, remote, { component, file, func });
				}
			}
			return remote;
		}

		// Offline
		if (offlineFunc) {
			this.offlineQueue.push({
				component, file, func, data,
				$baseHash: data?.$hash ?? null,
				timestamp: Date.now(),
			});
			const result = await offlineFunc(data, ...(args || []));
			if (result && typeof result === 'object') result.$offline = true;
			return result;
		}

		return { $offline: true };
	}

	_wsRequest(component, file, func, data, args) {
		return new Promise((resolve, reject) => {
			const id = createRandomIdentifier();
			webSocket.send({
				type: 2,
				id,
				component,
				file,
				func,
				data,
				args,
			});
			this.pending[id] = { resolve, reject, time: Date.now() };
		});
	}

	receive(payload, length) {
		if (payload.id) {
			if (this.pending[payload.id]) {
				this.pending[payload.id].resolve(payload.data);
				delete this.pending[payload.id];
			}
		} else if (payload.section) {
			if (receiver[payload.section]) {
				receiver[payload.section](payload.data);
			}
		}
	}

	async sync() {
		if (this.offlineQueue.length === 0) return;

		try {
			for (const entry of this.offlineQueue) {
				await this._wsRequest(entry.component, entry.file, entry.func, {
					...entry.data,
					$baseHash: entry.$baseHash,
				}, []);
			}
			this.offlineQueue = [];
		} catch (e) {
			console.error('[Hybrilior] Sync failed:', e);
		}
	}
}
